import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import client from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { renderMarkdown } from "@/lib/markdown";
import {
  ChevronLeft,
  Edit3,
  MessageSquarePlus,
  Clock,
  Plus,
  Trash2,
  History,
  X,
  RotateCcw,
  ShieldCheck,
  Languages,
} from "lucide-react";

export default function WikiPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await client.get(`/wiki/pages/${slug}`, { params: { language: i18n.language } });
        if (cancelled) return;
        setNotFound(false);
        setPage(r.data);
        const rr = await client.get("/wiki/pages", { params: { language: i18n.language } });
        if (cancelled) return;
        setSiblings(rr.data.filter(p => p.category === r.data.category && p.slug !== r.data.slug));
      } catch (e) {
        if (!cancelled && e?.response?.status === 404) {
          setPage(null);
          setNotFound(true);
        }
      }
    })();
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => { cancelled = true; };
  }, [slug, i18n.language]);

  const showPage = page && page.slug === slug;

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete "${page.title}"? This can be restored from revision history.`)) return;
    try {
      await client.delete(`/wiki/pages/${page.slug}`);
      navigate("/wiki");
    } catch (e) {
      alert("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-16 md:py-20 text-center" data-testid="wiki-not-found">
        <h1 className="font-display text-2xl md:text-4xl text-[var(--t-cyan)] mb-4 uppercase">Entry Lost in the Fog of War</h1>
        <p className="text-[var(--t-text-dim)] mb-6">This codex page does not exist yet.</p>
        <Link to="/wiki" className="btn-ghost">Back to Codex</Link>
      </div>
    );
  }

  if (!showPage) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-16 md:py-20 text-center" data-testid="wiki-loading">
        <div className="font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow">// loading dossier...</div>
      </div>
    );
  }

  const isAdmin = !!user?.is_admin;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10" data-testid="wiki-page">
      {/* Admin toolbar — only rendered when authenticated user has admin role */}
      {isAdmin && (
        <AdminPanel
          page={page}
          onEdit={() => navigate(`/admin/edit/${page.slug}`)}
          onCreate={() => navigate("/admin/new")}
          onDelete={handleDelete}
          onHistory={() => setHistoryOpen(true)}
        />
      )}

      <div className="grid lg:grid-cols-[1fr_280px] gap-8 lg:gap-10">
        <article className="min-w-0">
          <Link to="/wiki" className="inline-flex items-center gap-1 text-[10px] md:text-xs font-mono tracking-[0.2em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)] mb-5 md:mb-6" data-testid="back-link">
            <ChevronLeft className="w-3 h-3" /> {t("wiki.back")}
          </Link>

          {page._fallback_language && i18n.language !== page._fallback_language && (
            <div
              className="mb-5 md:mb-6 px-4 py-3 border border-[var(--t-blue)]/40 bg-[rgba(58,160,255,0.06)] flex items-start gap-3"
              data-testid="translation-fallback-banner"
            >
              <Languages className="w-4 h-4 text-[var(--t-blue-glow)] flex-shrink-0 mt-0.5" strokeWidth={1.7} />
              <div className="text-xs md:text-sm text-[var(--t-text-dim)] leading-relaxed">
                {t("wiki.fallbackBanner")}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-2 gap-3">
            <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase truncate">// {page.category}</span>
            <Link to={`/contribute?slug=${page.slug}`} className="btn-ghost !py-1.5 !px-2.5 md:!px-3 !text-[10px] flex-shrink-0" data-testid="suggest-edit">
              <MessageSquarePlus className="w-3 h-3" /> <span className="hidden sm:inline">Suggest</span>
            </Link>
          </div>

          <div className="prose-t" dangerouslySetInnerHTML={{ __html: renderMarkdown(page.content) }} />

          <div className="mt-10 md:mt-12 pt-6 border-t border-[var(--t-border)] flex flex-col sm:flex-row items-start sm:items-center justify-between text-[10px] md:text-xs font-mono text-[var(--t-text-muted)] tracking-[0.16em] md:tracking-[0.18em] uppercase gap-2">
            <span className="flex items-center gap-2"><Clock className="w-3 h-3" /> Updated {page.updated_at ? new Date(page.updated_at).toLocaleDateString() : "—"}</span>
            <span className="break-all">by {page.updated_by || "system"}</span>
          </div>
        </article>

        <aside className="lg:sticky lg:top-6 self-start space-y-4" data-testid="sidebar">
          <div className="panel p-5">
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-3">// Related</div>
            <ul className="space-y-2">
              {siblings.map(s => (
                <li key={s.slug}>
                  <Link to={`/wiki/${s.slug}`} className="text-sm text-[var(--t-steel)] hover:text-[var(--t-cyan)] transition block py-1" data-testid={`related-${s.slug}`}>
                    {s.title}
                  </Link>
                </li>
              ))}
              {siblings.length === 0 && <li className="text-xs text-[var(--t-text-muted)]">No related entries yet.</li>}
            </ul>
          </div>
          <div className="panel p-5">
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-3">// Quick Links</div>
            <div className="flex flex-col gap-2">
              <a href="https://territoria.world" target="_blank" rel="noreferrer" className="btn-primary !py-2 !text-xs !justify-center" data-testid="sidebar-play">Play Now</a>
              <a href="https://discord.gg/territoria" target="_blank" rel="noreferrer" className="btn-ghost !py-2 !text-xs !justify-center" data-testid="sidebar-discord">Join Discord</a>
            </div>
          </div>
        </aside>
      </div>

      {isAdmin && historyOpen && (
        <RevisionHistorySheet
          slug={page.slug}
          onClose={() => setHistoryOpen(false)}
          onRestored={(restored) => {
            setPage(restored);
            setHistoryOpen(false);
          }}
        />
      )}
    </div>
  );
}

// =====================================================
// Admin toolbar (top-right)
// =====================================================
function AdminPanel({ page, onEdit, onCreate, onDelete, onHistory }) {
  return (
    <div className="flex justify-end mb-5 md:mb-6" data-testid="wiki-admin-panel">
      <div className="panel inline-flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1.5 md:py-2">
        <span className="hidden md:inline-flex items-center gap-1.5 pr-2 mr-1 border-r border-[var(--t-border)]">
          <ShieldCheck className="w-3.5 h-3.5 text-[var(--t-blue-glow)]" />
          <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">Admin</span>
        </span>
        <AdminButton onClick={onEdit} icon={Edit3} label="Edit Page" testid="admin-edit-page" />
        <AdminButton onClick={onCreate} icon={Plus} label="Create Page" testid="admin-create-page" />
        <AdminButton onClick={onHistory} icon={History} label="History" testid="admin-history" />
        <AdminButton onClick={onDelete} icon={Trash2} label="Delete" testid="admin-delete-page" danger />
      </div>
    </div>
  );
}

function AdminButton({ onClick, icon: Icon, label, testid, danger }) {
  const dangerCls = danger
    ? "border-[var(--t-red)] text-[var(--t-red)] hover:bg-[rgba(227,90,90,0.08)]"
    : "border-[var(--t-border)] text-[var(--t-text-dim)] hover:text-[var(--t-cyan)] hover:border-[var(--t-blue)] hover:bg-[rgba(58,160,255,0.08)]";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={label}
      className={`inline-flex items-center gap-1.5 px-2 md:px-2.5 py-1 md:py-1.5 text-[10px] md:text-[11px] font-mono tracking-[0.18em] uppercase border transition ${dangerCls}`}
    >
      <Icon className="w-3 h-3 md:w-3.5 md:h-3.5 flex-shrink-0" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// =====================================================
// Revision history side sheet
// =====================================================
function RevisionHistorySheet({ slug, onClose, onRestored }) {
  const [revisions, setRevisions] = useState(null);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    client.get(`/wiki/pages/${slug}/revisions`)
      .then(r => setRevisions(r.data))
      .catch(() => setRevisions([]));
  }, [slug]);

  const restore = async (rev) => {
    if (!window.confirm(`Restore the version saved ${formatDate(rev.saved_at)} by ${rev.saved_by_name || "unknown"}? Current content will be preserved as a revision.`)) return;
    setRestoring(rev.id);
    try {
      const r = await client.post(`/wiki/pages/${slug}/revisions/${rev.id}/restore`);
      onRestored(r.data);
    } catch (e) {
      alert("Restore failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60]" data-testid="revision-history-sheet">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      {/* sheet */}
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[440px] panel border-l border-[var(--t-border-hi)] overflow-hidden flex flex-col" data-testid="revision-history-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--t-border)] flex-shrink-0">
          <div>
            <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">// Revision History</span>
            <div className="font-display text-lg md:text-xl text-[var(--t-text)] uppercase leading-tight mt-1">{slug}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 inline-flex items-center justify-center border border-[var(--t-border)] hover:border-[var(--t-blue)] hover:text-[var(--t-cyan)] text-[var(--t-text-muted)]" data-testid="history-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {revisions === null && (
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--t-blue-glow)] uppercase pulse-glow">// loading...</div>
          )}
          {revisions && revisions.length === 0 && (
            <div className="text-center py-10">
              <Clock className="w-10 h-10 text-[var(--t-text-muted)] mx-auto mb-3" />
              <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase mb-1">// No revisions yet</div>
              <p className="text-xs text-[var(--t-text-dim)]">Edits made from now on will be snapshotted here.</p>
            </div>
          )}
          {revisions && revisions.length > 0 && (
            <ul className="space-y-3">
              {revisions.map(r => (
                <li key={r.id} className="panel p-3 border-l-2 border-[var(--t-border-hi)]" data-testid={`revision-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <ActionBadge action={r.action} />
                        <span className="font-mono text-[10px] tracking-[0.18em] text-[var(--t-text-muted)] uppercase">{formatDate(r.saved_at)}</span>
                      </div>
                      <div className="font-display text-sm text-[var(--t-text)] uppercase truncate">{r.title}</div>
                      <div className="font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.12em] mt-0.5 truncate">{r.category} · {r.saved_by_name || "unknown"}</div>
                      {r.summary && (
                        <p className="text-xs text-[var(--t-text-dim)] mt-2 line-clamp-2">{r.summary}</p>
                      )}
                    </div>
                    <button
                      onClick={() => restore(r)}
                      disabled={!!restoring}
                      className="btn-ghost !py-1.5 !px-2.5 !text-[10px] flex-shrink-0"
                      data-testid={`restore-${r.id}`}
                      title="Restore this version"
                    >
                      <RotateCcw className="w-3 h-3" /> {restoring === r.id ? "..." : "Restore"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

function ActionBadge({ action }) {
  const map = {
    update:  { label: "Edit",    color: "var(--t-blue-glow)" },
    delete:  { label: "Delete",  color: "var(--t-red)" },
    restore: { label: "Restore", color: "var(--t-gold)" },
  };
  const m = map[action] || { label: action || "—", color: "var(--t-text-muted)" };
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 font-mono text-[9px] tracking-[0.2em] uppercase border"
      style={{ color: m.color, borderColor: m.color }}
    >
      {m.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
